import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type View = 'student' | 'teacher' | 'parent';
export type Lang = 'bn' | 'en';

interface UiState {
  /** Mobile drawer (below 900px). */
  menuOpen: boolean; setMenuOpen: (v: boolean) => void;
  /** Desktop: hide the 232px OS sidebar; the header hamburger restores it (same pattern as the sibling apps). */
  sidebarCollapsed: boolean; setSidebarCollapsed: (v: boolean) => void;
  /** Which perspective the sidebar shows (a person may hold several roles). */
  view: View | null; setView: (v: View) => void;
  /** Which learner the screens are about (teachers and parents pick; students are fixed to themselves). */
  studentId: string | null; setStudentId: (id: string | null) => void;
  /** Numerals: Bangla or English. Bangla copy always shows; this only switches digits and the secondary line's emphasis. */
  lang: Lang; setLang: (l: Lang) => void;
  /** Khata focus mode: sidebar hidden, focus banner with the clock (design §1 "Khata full-screen mode"). */
  focusSince: number | null; setFocus: (on: boolean) => void;
  /** Per-screen filters (tab, day, chapter …). Selections are transient and never persisted. */
  pages: Record<string, Record<string, unknown>>;
  setPage: (page: string, patch: Record<string, unknown>) => void;
}

const TRANSIENT = new Set(['selectedId', 'mode', 'sub', 'drill']);
const persistedPages = (pages: UiState['pages']) => Object.fromEntries(Object.entries(pages).map(([page, s]) => [page, Object.fromEntries(Object.entries(s).filter(([k]) => !TRANSIENT.has(k)))]));

export const useUi = create<UiState>()(persist((set) => ({
  menuOpen: false, setMenuOpen: (menuOpen) => set({ menuOpen }),
  sidebarCollapsed: false, setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
  view: null, setView: (view) => set({ view }),
  studentId: null, setStudentId: (studentId) => set({ studentId }),
  lang: 'bn', setLang: (lang) => set({ lang }),
  focusSince: null, setFocus: (on) => set({ focusSince: on ? Date.now() : null, sidebarCollapsed: on }),
  pages: {},
  setPage: (page, patch) => set((s) => ({ pages: { ...s.pages, [page]: { ...(s.pages[page] ?? {}), ...patch } } })),
}), { name: 'shikkahsetu.ui', partialize: (s) => ({ sidebarCollapsed: s.sidebarCollapsed, view: s.view, studentId: s.studentId, lang: s.lang, pages: persistedPages(s.pages) }) }));

export function usePageState<T extends Record<string, unknown>>(page: string, defaults: T): [T, (patch: Partial<T>) => void] {
  const state = useUi((s) => s.pages[page]) as Partial<T> | undefined;
  const setPage = useUi((s) => s.setPage);
  return [{ ...defaults, ...(state ?? {}) }, (patch) => setPage(page, patch)];
}
